import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import QuestionList from '../QuestionList';
import axios from '../../../api/axios';

function StudentQuestionsTab({
  questions,
  loading,
  expandedSections,
  onSectionToggle,
  sortBy,
  onSortChange,
  getStatusIcon,
  onQuestionClick
}) {
  const [notebookOutline, setNotebookOutline] = useState(null);
  const [viewMode, setViewMode] = useState('section'); // 'section' | 'type' | 'difficulty'
  const [searchQuery, setSearchQuery] = useState('');

  // Check if all questions are from the same notebook (notebook category selected)
  const isNotebookCategory = questions.length > 0 && questions.every(q => q.isNotebookExample);
  const notebookTitle = isNotebookCategory && questions.length > 0 
    ? questions[0].notebookTitle || questions[0].category 
    : null;
  const notebookId = isNotebookCategory && questions.length > 0 
    ? questions[0].notebookId 
    : null;

  // Fetch notebook outline to get section order
  useEffect(() => {
    const fetchNotebookOutline = async () => {
      if (!notebookId) {
        setNotebookOutline(null);
        return;
      }

      try {
        // Get notebook by notebookId (string identifier)
        const response = await axios.get(`/notes/${notebookId}`);
        if (response.data && response.data.outline) {
          setNotebookOutline(response.data.outline);
        }
      } catch (error) {
        // If failed to fetch, just log the error and continue without outline
        console.warn('Failed to fetch notebook outline:', error);
        setNotebookOutline(null);
      }
    };

    if (notebookId) {
      fetchNotebookOutline();
    }
  }, [notebookId]);

  // 搜索过滤：按标题或题目内容
  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return questions;
    const q = searchQuery.trim().toLowerCase();
    return questions.filter(
      (question) =>
        (question.title || '').toLowerCase().includes(q) ||
        (question.question || '').toLowerCase().includes(q)
    );
  }, [questions, searchQuery]);

  // Group notebook questions by section（使用过滤后的列表）
  const getGroupedBySection = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const sectionTitle = q.sectionTitle || '其他';
      if (!grouped[sectionTitle]) grouped[sectionTitle] = [];
      grouped[sectionTitle].push(q);
    });
    return grouped;
  };

  const groupedBySection = getGroupedBySection(filteredQuestions);

  // 按题目类型分组（用于「按题目类型」Tab）
  const QUESTION_TYPE_LABELS = {
    multiple_choice: '选择题',
    short_answer: '简答题',
    proof: '证明题',
    code: '代码题',
    code_tracing: '代码追踪题',
  };
  const QUESTION_TYPE_ORDER = ['multiple_choice', 'short_answer', 'proof', 'code', 'code_tracing'];

  const getGroupedByType = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const type = q.questionType || '其他';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(q);
    });
    return grouped;
  };

  const groupedByType = getGroupedByType(filteredQuestions);
  const typeKeys = QUESTION_TYPE_ORDER.filter(k => groupedByType[k]?.length).concat(
    Object.keys(groupedByType).filter(k => !QUESTION_TYPE_ORDER.includes(k))
  );

  // 按难度分组（用于「按难度筛选」Tab）
  const DIFFICULTY_LABELS = { easy: '简单', medium: '中等', hard: '困难', exam: '考试原题' };
  const DIFFICULTY_ORDER = ['easy', 'medium', 'hard', 'exam'];
  const getGroupedByDifficulty = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const d = q.difficulty || 'easy';
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(q);
    });
    return grouped;
  };
  const groupedByDifficulty = getGroupedByDifficulty(filteredQuestions);
  const difficultyKeys = DIFFICULTY_ORDER.filter(k => groupedByDifficulty[k]?.length).concat(
    Object.keys(groupedByDifficulty).filter(k => !DIFFICULTY_ORDER.includes(k))
  );

  // Sort section keys by notebook outline order (outline.outlines 的键顺序即章节顺序)
  const getSortedSectionKeys = () => {
    const keys = Object.keys(groupedBySection);
    const outlineSectionOrder = notebookOutline?.outlines && typeof notebookOutline.outlines === 'object'
      ? Object.keys(notebookOutline.outlines)
      : null;

    if (!outlineSectionOrder || outlineSectionOrder.length === 0) {
      return keys;
    }

    // 按 outline.outlines 中的出现顺序排序
    return keys.sort((a, b) => {
      const indexA = outlineSectionOrder.indexOf(a);
      const indexB = outlineSectionOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const sectionKeys = getSortedSectionKeys();

  // Calculate statistics for the notebook
  const totalQuestions = questions.length;
  const completedQuestions = questions.filter(q => q.userStatus?.status === 'passed').length;

  // Calculate statistics for each section
  const getSectionStats = (sectionQuestions) => {
    const total = sectionQuestions.length;
    const passed = sectionQuestions.filter(q => q.userStatus?.status === 'passed').length;
    
    // Calculate score statistics
    // Note: Some questions use 0-100 scale (multiple choice, code tracing), 
    // while others use 0-10 scale (short answer, proof with AI grading)
    // Normalize all scores to 0-10 scale for consistent calculation
    const normalizeScore = (score) => {
      // If score > 10, assume it's on 0-100 scale and convert to 0-10
      return score > 10 ? score / 10 : score;
    };

    const questionsWithScores = sectionQuestions.filter(q => q.userStatus?.score !== undefined && q.userStatus?.score !== null);
    
    // Normalize scores to 0-10 scale before calculating
    const normalizedScores = questionsWithScores.map(q => normalizeScore(q.userStatus?.score || 0));
    const totalScore = normalizedScores.reduce((sum, score) => sum + score, 0);
    const averageScore = questionsWithScores.length > 0 
      ? Math.round((totalScore / questionsWithScores.length) * 10) / 10 // Round to 1 decimal place
      : 0;
    const maxScore = total * 10; // Max score per question is 10 (normalized)
    
    return { 
      total, 
      passed,
      averageScore,
      totalScore: Math.round(totalScore * 10) / 10, // Round to 1 decimal place
      maxScore,
      attempted: questionsWithScores.length
    };
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Only show title and sort when not viewing a notebook */}
      {!notebookTitle && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography component="h2" variant="h6">
              全部题目
            </Typography>
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              sx={{ minWidth: 150 }}
              label="排序方式"
            >
              <MenuItem value="questionNumber">按题号</MenuItem>
              <MenuItem value="difficulty">按难度</MenuItem>
              <MenuItem value="title">按标题</MenuItem>
              <MenuItem value="status">按状态</MenuItem>
            </TextField>
          </Stack>
        </Box>
      )}

      {notebookTitle ? (
        /* Show notebook questions grouped by section */
        questions.length === 0 ? (
          <Card variant="outlined">
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                该笔记本暂无练习题
              </Typography>
            </Box>
          </Card>
        ) : (
          <Box>
            {/* Notebook Overview Poster */}
            <Paper
              sx={{
                position: 'relative',
                backgroundColor: 'grey.800',
                color: '#fff',
                mb: 4,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundImage: `url(https://picsum.photos/seed/${encodeURIComponent(notebookTitle)}/1200/400)`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <img 
                style={{ display: 'none' }} 
                src={`https://picsum.photos/seed/${encodeURIComponent(notebookTitle)}/1200/400`} 
                alt={notebookTitle} 
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: 0,
                  backgroundColor: 'rgba(0,0,0,.4)',
                }}
              />
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: 'relative',
                      p: { xs: 3, md: 6 },
                      pr: { md: 0 },
                    }}
                  >
                    <Typography
                      component="h1"
                      variant="h3"
                      color="inherit"
                      gutterBottom
                      fontWeight="bold"
                    >
                      {notebookTitle}
                    </Typography>
                    <Typography
                      variant="h5"
                      color="inherit"
                      sx={{
                        opacity: 0.95,
                        mt: 2,
                        fontWeight: 500,
                      }}
                    >
                      已完成 {completedQuestions} / {totalQuestions} 题
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={viewMode}
                onChange={(_, v) => setViewMode(v)}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Tab label="按章节" value="section" />
                <Tab label="按题目类型" value="type" />
                <Tab label="按难度筛选" value="difficulty" />
              </Tabs>
              <TextField
                size="small"
                placeholder="搜索题目"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 160, flexShrink: 0 }}
                inputProps={{ 'aria-label': '搜索题目' }}
              />
            </Box>

            {viewMode === 'section' && sectionKeys.map((sectionTitle) => {
              const sectionQuestions = groupedBySection[sectionTitle];
              // Use notebook title and section title as key
              const sectionKey = `${notebookTitle}_${sectionTitle}`;
              const isSectionExpanded = expandedSections[sectionKey] || false;
              const sectionStats = getSectionStats(sectionQuestions);
              
              return (
                <Accordion
                  key={sectionKey}
                  expanded={isSectionExpanded}
                  onChange={() => onSectionToggle(sectionKey)}
                  sx={{ 
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isSectionExpanded 
                      ? '0 2px 8px rgba(0,0,0,0.1)' 
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isSectionExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': {
                      display: 'none',
                    },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: isSectionExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {sectionTitle}
                        </Typography>
                        {!isSectionExpanded && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              做对 {sectionStats.passed} / {sectionStats.total} 题
                            </Typography>
                            {sectionStats.attempted > 0 && (
                              <Typography
                                variant="caption"
                                color={sectionStats.averageScore >= 7 ? 'success.main' : sectionStats.averageScore >= 5 ? 'warning.main' : 'error.main'}
                                sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}
                              >
                                平均分：{sectionStats.averageScore}/10 · 总分：{sectionStats.totalScore}/{sectionStats.maxScore} 分
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Chip
                        label={`${sectionQuestions.length} 题`}
                        size="small"
                        color={isSectionExpanded ? 'primary' : 'default'}
                        variant={isSectionExpanded ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 500,
                          transition: 'all 0.2s ease-in-out',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={sectionQuestions}
                      sortBy={sortBy}
                      showActions={false}
                      isNotebookExample={isNotebookCategory}
                      getStatusIcon={getStatusIcon}
                      onQuestionClick={onQuestionClick}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {viewMode === 'type' && typeKeys.map((questionType) => {
              const typeQuestions = groupedByType[questionType];
              const typeLabel = QUESTION_TYPE_LABELS[questionType] || questionType;
              const typeKey = `type_${notebookTitle}_${questionType}`;
              const isTypeExpanded = expandedSections[typeKey] || false;
              const typeStats = getSectionStats(typeQuestions);

              return (
                <Accordion
                  key={typeKey}
                  expanded={isTypeExpanded}
                  onChange={() => onSectionToggle(typeKey)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isTypeExpanded
                      ? '0 2px 8px rgba(0,0,0,0.1)'
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isTypeExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: isTypeExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {typeLabel}
                        </Typography>
                        {!isTypeExpanded && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              做对 {typeStats.passed} / {typeStats.total} 题
                            </Typography>
                            {typeStats.attempted > 0 && (
                              <Typography
                                variant="caption"
                                color={typeStats.averageScore >= 7 ? 'success.main' : typeStats.averageScore >= 5 ? 'warning.main' : 'error.main'}
                                sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}
                              >
                                平均分：{typeStats.averageScore}/10 · 总分：{typeStats.totalScore}/{typeStats.maxScore} 分
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Chip
                        label={`${typeQuestions.length} 题`}
                        size="small"
                        color={isTypeExpanded ? 'primary' : 'default'}
                        variant={isTypeExpanded ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 500, transition: 'all 0.2s ease-in-out' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={typeQuestions}
                      sortBy={sortBy}
                      showActions={false}
                      isNotebookExample={isNotebookCategory}
                      getStatusIcon={getStatusIcon}
                      onQuestionClick={(q, ctx) => onQuestionClick(q, { ...(ctx || {}), fromView: 'type' })}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {viewMode === 'difficulty' && difficultyKeys.map((difficultyKey) => {
              const difficultyQuestions = groupedByDifficulty[difficultyKey];
              const difficultyLabel = DIFFICULTY_LABELS[difficultyKey] || difficultyKey;
              const difficultyKeyId = `difficulty_${notebookTitle}_${difficultyKey}`;
              const isDifficultyExpanded = expandedSections[difficultyKeyId] || false;
              const difficultyStats = getSectionStats(difficultyQuestions);

              return (
                <Accordion
                  key={difficultyKeyId}
                  expanded={isDifficultyExpanded}
                  onChange={() => onSectionToggle(difficultyKeyId)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isDifficultyExpanded
                      ? '0 2px 8px rgba(0,0,0,0.1)'
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isDifficultyExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: isDifficultyExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {difficultyLabel}
                        </Typography>
                        {!isDifficultyExpanded && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              做对 {difficultyStats.passed} / {difficultyStats.total} 题
                            </Typography>
                            {difficultyStats.attempted > 0 && (
                              <Typography
                                variant="caption"
                                color={difficultyStats.averageScore >= 7 ? 'success.main' : difficultyStats.averageScore >= 5 ? 'warning.main' : 'error.main'}
                                sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}
                              >
                                平均分：{difficultyStats.averageScore}/10 · 总分：{difficultyStats.totalScore}/{difficultyStats.maxScore} 分
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Chip
                        label={`${difficultyQuestions.length} 题`}
                        size="small"
                        color={isDifficultyExpanded ? 'primary' : 'default'}
                        variant={isDifficultyExpanded ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 500, transition: 'all 0.2s ease-in-out' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={difficultyQuestions}
                      sortBy={sortBy}
                      showActions={false}
                      isNotebookExample={isNotebookCategory}
                      getStatusIcon={getStatusIcon}
                      onQuestionClick={(q, ctx) => onQuestionClick(q, { ...(ctx || {}), fromView: 'difficulty' })}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )
      ) : (
        /* Show all questions (regular + notebook) */
        questions.length === 0 ? (
          <Card variant="outlined">
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                暂无题目
              </Typography>
            </Box>
          </Card>
        ) : (
          <QuestionList
            questions={questions}
            sortBy={sortBy}
            showActions={false}
            isNotebookExample={questions.length > 0 && questions.every((q) => q.isNotebookExample)}
            getStatusIcon={getStatusIcon}
            onQuestionClick={onQuestionClick}
          />
        )
      )}
    </Container>
  );
}

export default StudentQuestionsTab;
